import { MutableRefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export const MIN_ZOOM = 0.5;
export const MAX_ZOOM = 2;
const ZOOM_STEP = 0.25;
/** Converts ctrl+wheel / trackpad-pinch `deltaY` into a zoom factor. */
const WHEEL_ZOOM_SPEED = 0.01;
const ZOOM_EPSILON = 0.005;

export const clampZoom = (zoom: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));

/** The next button stop (multiples of the step), so a pinched 113% steps to 125% / 100%. */
export const getSteppedZoom = (zoom: number, direction: 1 | -1) => {
  const index = zoom / ZOOM_STEP;
  const next = direction > 0 ? Math.floor(index + ZOOM_EPSILON) + 1 : Math.ceil(index - ZOOM_EPSILON) - 1;
  return clampZoom(next * ZOOM_STEP);
};

/** A point on a page (as page fractions) and where it should stay on screen (relative to the scroll root). */
interface ZoomAnchor {
  pageIndex: number;
  fractionX: number;
  fractionY: number;
  x: number;
  y: number;
}

interface PinchState {
  startDistance: number;
  startZoom: number;
  startX: number;
  startY: number;
  x: number;
  y: number;
  zoom: number;
}

const getTouchDistance = (touches: TouchList) =>
  Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);

const getTouchMidpoint = (touches: TouchList) => ({
  x: (touches[0].clientX + touches[1].clientX) / 2,
  y: (touches[0].clientY + touches[1].clientY) / 2,
});

interface UsePdfZoomArgs {
  scrollRoot: HTMLElement | null;
  /** Element wrapping all pages; scaled with a CSS transform while pinching. */
  content: HTMLElement | null;
  pageElements: MutableRefObject<Map<number, HTMLElement>>;
}

/**
 * Zoom for the PDF viewer: buttons, pinch (touch) and ctrl+wheel / trackpad pinch. Panning stays native scrolling.
 *
 * A pinch scales the content with a transform (no re-render per frame) and commits the zoom once the fingers lift.
 * Every zoom keeps the page point under the focal point (fingers, cursor or viewport centre) in place.
 */
export const usePdfZoom = ({ scrollRoot, content, pageElements }: UsePdfZoomArgs) => {
  const [zoom, setZoom] = useState(1);
  // Latest requested zoom, ahead of state while several wheel events arrive before a render.
  const zoomRef = useRef(1);
  const pendingAnchor = useRef<ZoomAnchor | null>(null);

  const getAnchor = useCallback(
    (clientX: number, clientY: number): ZoomAnchor | null => {
      if (!scrollRoot) return null;
      let nearest: { pageIndex: number; rect: DOMRect; distance: number } | undefined;
      for (const [pageIndex, element] of pageElements.current) {
        const rect = element.getBoundingClientRect();
        const distance = clientY < rect.top ? rect.top - clientY : Math.max(0, clientY - rect.bottom);
        if (!nearest || distance < nearest.distance) nearest = { pageIndex, rect, distance };
      }
      if (!nearest || nearest.rect.width <= 0 || nearest.rect.height <= 0) return null;
      const rootRect = scrollRoot.getBoundingClientRect();
      return {
        pageIndex: nearest.pageIndex,
        fractionX: (clientX - nearest.rect.left) / nearest.rect.width,
        fractionY: (clientY - nearest.rect.top) / nearest.rect.height,
        x: clientX - rootRect.left,
        y: clientY - rootRect.top,
      };
    },
    [scrollRoot, pageElements]
  );

  const applyAnchor = useCallback(
    (anchor: ZoomAnchor | null) => {
      const page = anchor && pageElements.current.get(anchor.pageIndex);
      if (!scrollRoot || !anchor || !page) return;
      const rootRect = scrollRoot.getBoundingClientRect();
      const rect = page.getBoundingClientRect();
      scrollRoot.scrollLeft += rect.left + anchor.fractionX * rect.width - (rootRect.left + anchor.x);
      scrollRoot.scrollTop += rect.top + anchor.fractionY * rect.height - (rootRect.top + anchor.y);
    },
    [scrollRoot, pageElements]
  );

  const resetPinchTransform = useCallback(() => {
    if (!content) return;
    content.style.transform = '';
    content.style.transformOrigin = '';
  }, [content]);

  /** Zooms to `next` around a client point (defaults to the centre of the scroll root). */
  const zoomTo = useCallback(
    (next: number, clientX?: number, clientY?: number) => {
      if (!scrollRoot) return;
      const rootRect = scrollRoot.getBoundingClientRect();
      const anchor = getAnchor(
        clientX ?? rootRect.left + rootRect.width / 2,
        clientY ?? rootRect.top + rootRect.height / 2
      );
      const target = clampZoom(next);
      if (Math.abs(target - zoomRef.current) < ZOOM_EPSILON) {
        // Nothing to re-lay out; still settle any pinch transform (it may include a pan).
        resetPinchTransform();
        applyAnchor(anchor);
        return;
      }
      zoomRef.current = target;
      pendingAnchor.current = anchor;
      setZoom(target);
    },
    [scrollRoot, getAnchor, applyAnchor, resetPinchTransform]
  );

  // Pages have their new size here; drop the pinch transform and restore the focal point before paint.
  useLayoutEffect(() => {
    resetPinchTransform();
    applyAnchor(pendingAnchor.current);
    pendingAnchor.current = null;
  }, [zoom, resetPinchTransform, applyAnchor]);

  useEffect(() => {
    if (!scrollRoot || !content) return;
    let pinch: PinchState | null = null;

    const handleTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 2) return;
      const { x, y } = getTouchMidpoint(event.touches);
      const contentRect = content.getBoundingClientRect();
      content.style.transformOrigin = `${x - contentRect.left}px ${y - contentRect.top}px`;
      content.dataset.pinching = 'true';
      pinch = {
        startDistance: Math.max(getTouchDistance(event.touches), 1),
        startZoom: zoomRef.current,
        startX: x,
        startY: y,
        x,
        y,
        zoom: zoomRef.current,
      };
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (!pinch || event.touches.length !== 2) return;
      // Stop the browser from zooming the whole page.
      event.preventDefault();
      const { x, y } = getTouchMidpoint(event.touches);
      pinch.zoom = clampZoom((pinch.startZoom * getTouchDistance(event.touches)) / pinch.startDistance);
      pinch.x = x;
      pinch.y = y;
      const scale = pinch.zoom / pinch.startZoom;
      content.style.transform = `translate(${x - pinch.startX}px, ${y - pinch.startY}px) scale(${scale})`;
    };

    const handleTouchEnd = (event: TouchEvent) => {
      if (!pinch || event.touches.length >= 2) return;
      const { zoom: nextZoom, x, y } = pinch;
      pinch = null;
      delete content.dataset.pinching;
      // Measured while still transformed, so the point under the fingers stays put once the zoom is committed.
      zoomTo(nextZoom, x, y);
    };

    const handleWheel = (event: WheelEvent) => {
      // Trackpad pinch arrives as ctrl+wheel; plain wheel keeps scrolling.
      if (!event.ctrlKey) return;
      event.preventDefault();
      zoomTo(zoomRef.current * Math.exp(-event.deltaY * WHEEL_ZOOM_SPEED), event.clientX, event.clientY);
    };

    scrollRoot.addEventListener('touchstart', handleTouchStart, { passive: true });
    scrollRoot.addEventListener('touchmove', handleTouchMove, { passive: false });
    scrollRoot.addEventListener('touchend', handleTouchEnd);
    scrollRoot.addEventListener('touchcancel', handleTouchEnd);
    scrollRoot.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      scrollRoot.removeEventListener('touchstart', handleTouchStart);
      scrollRoot.removeEventListener('touchmove', handleTouchMove);
      scrollRoot.removeEventListener('touchend', handleTouchEnd);
      scrollRoot.removeEventListener('touchcancel', handleTouchEnd);
      scrollRoot.removeEventListener('wheel', handleWheel);
      delete content.dataset.pinching;
      resetPinchTransform();
    };
  }, [scrollRoot, content, zoomTo, resetPinchTransform]);

  return {
    zoom,
    canZoomIn: zoom < MAX_ZOOM - ZOOM_EPSILON,
    canZoomOut: zoom > MIN_ZOOM + ZOOM_EPSILON,
    zoomIn: () => zoomTo(getSteppedZoom(zoomRef.current, 1)),
    zoomOut: () => zoomTo(getSteppedZoom(zoomRef.current, -1)),
  };
};
